import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { callEdgeFunction, isEdgeFunctionMigrated, getEdgeFunctionName } from '../lib/edgeFunctionClient';

interface PerformanceScorecardInlineProps {
  flowId: string;
  flowName: string;
}

interface Scorecard {
  flowId: string;
  flowName: string;
  grade: string;
  score: number;
  metrics: {
    queryRisk: 'LOW' | 'MEDIUM' | 'HIGH';
    dmlRisk: 'LOW' | 'MEDIUM' | 'HIGH';
    bulkSafety: 'PASS' | 'WARNING' | 'FAIL';
    cpuRisk: 'LOW' | 'MEDIUM' | 'HIGH';
  };
  details: {
    queriesInLoops: number;
    dmlInLoops: number;
    totalQueries: number;
    hasErrorHandling: boolean;
    isBulkified: boolean;
    elementCount: number;
    totalDmlOperations: number;
  };
  recommendations: Array<{
    priority: number;
    impact: string;
    title: string;
    description: string;
    specificSteps?: Array<{
      element?: string;
      action: string;
      helpUrl?: string;
    }>;
    estimatedImprovement?: string;
  }>;
  summary: string;
  issues: Array<{
    severity: string;
    type: string;
    description: string;
    impact?: string;
    metadata?: {
      ids?: Array<[string, string]>;
      firstId?: string;
      firstType?: string;
      detailsExpanded?: string;
      fieldCheckCount?: number;
      fields?: string[];
    };
    detectorName?: string;
  }>;
  gradingDetails: {
    startingScore: number;
    deductions: Array<{
      reason: string;
      points: number;
      severity: 'CRITICAL' | 'MAJOR' | 'MINOR' | 'INFO';
      details: string;
    }>;
    bonuses: Array<any>;
    finalScore: number;
    grade: string;
    gradeThresholds: {
      'A': string;
      'B': string;
      'C': string;
      'D': string;
      'F': string;
    };
  };
  analyzedAt: string;
}

export default function PerformanceScorecardInline({ flowId, flowName }: PerformanceScorecardInlineProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scorecard, setScorecard] = useState<Scorecard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'grading' | 'issues'>('grading');
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    generateScorecard();
  }, [flowId]);


  const generateScorecard = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get the current session to get the access token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('No active session found');
      }

      let data;
      const endpoint = '/api/salesforce/performance-scorecard';
      
      if (isEdgeFunctionMigrated(endpoint)) {
        const functionName = getEdgeFunctionName(endpoint);
        if (functionName) {
          data = await callEdgeFunction(functionName, { flowId });
        }
      } else {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ flowId })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to generate scorecard');
        }

        data = await response.json();
      }
      
      setScorecard(data.scorecard);
    } catch (err) {
      console.error('Error generating scorecard:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate scorecard');
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A': return 'text-green-600 bg-green-100';
      case 'B': return 'text-blue-600 bg-blue-100';
      case 'C': return 'text-yellow-600 bg-yellow-100';
      case 'D': return 'text-orange-600 bg-orange-100';
      case 'F': return 'text-gray-600 bg-gray-100';
      case 'I': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-500 bg-gray-100';
    }
  };
  
  const getGradeBackgroundColor = (grade: string) => {
    switch (grade) {
      case 'A': return 'bg-green-500';
      case 'B': return 'bg-blue-500';
      case 'C': return 'bg-yellow-500';
      case 'D': return 'bg-orange-500';
      case 'F': return 'bg-gray-500';
      case 'I': return 'bg-gray-500';
      default: return 'bg-gray-400';
    }
  };
  
  const getImpactBadge = (points: number) => {
    const absPoints = Math.abs(points);
    
    if (absPoints >= 999) return 'showstopper';
    if (absPoints >= 16) return 'major';
    if (absPoints >= 10) return 'moderate';
    if (absPoints >= 1) return 'minor';
    return 'info';
  };
  
  const getBadgeClasses = (points: number) => {
    const badge = getImpactBadge(points);
    
    switch (badge) {
      case 'showstopper':
        return 'bg-purple-100 text-purple-700';
      case 'major':
        return 'bg-red-100 text-red-700';
      case 'moderate':
        return 'bg-orange-100 text-orange-700';
      case 'minor':
        return 'bg-yellow-100 text-yellow-700';
      case 'info':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'LOW': return 'text-green-600 bg-green-100';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
      case 'HIGH': return 'text-red-600 bg-red-100';
      case 'PASS': return 'text-green-600 bg-green-100';
      case 'WARNING': return 'text-yellow-600 bg-yellow-100';
      case 'FAIL': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'SHOWSTOPPER': return 'text-purple-700 bg-purple-100 border-purple-200';
      case 'CRITICAL': return 'text-red-700 bg-red-100 border-red-200';
      case 'MAJOR': return 'text-orange-700 bg-orange-100 border-orange-200';
      case 'MODERATE': return 'text-amber-700 bg-amber-100 border-amber-200';
      case 'MINOR': return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      default: return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Analysis</h3>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error generating scorecard</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
        <button
          onClick={generateScorecard}
          className="mt-4 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!scorecard) return null;

  return (
    <div className="bg-white rounded-lg shadow mt-6">
      {/* Collapsible Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between text-left"
        >
          <h3 className="text-lg font-medium text-gray-900">Performance Analysis</h3>
          <svg
            className={`h-5 w-5 text-gray-400 transform transition-transform ${isExpanded ? '' : 'rotate-180'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {isExpanded && (
        <>
          {/* Grade and Metrics */}
          <div className="px-6 py-4 bg-gray-50">
            <div className="grid grid-cols-5 gap-4">
              {/* Overall Grade - Now in a box like the others */}
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">Overall Grade</p>
                <div className={`mt-2 inline-flex items-center justify-center w-16 h-16 rounded-full text-3xl font-bold text-white ${getGradeBackgroundColor(scorecard.grade)}`}>
                  {scorecard.grade === 'F' ? 'I' : scorecard.grade}
                </div>
              </div>

              {/* Risk Metrics */}
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">Query Risk</p>
                <p className={`text-lg font-semibold px-3 py-1 rounded-full inline-block ${getRiskColor(scorecard.metrics.queryRisk)}`} style={{ marginTop: '1.125rem' }}>
                  {scorecard.metrics.queryRisk}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">DML Risk</p>
                <p className={`text-lg font-semibold px-3 py-1 rounded-full inline-block ${getRiskColor(scorecard.metrics.dmlRisk)}`} style={{ marginTop: '1.125rem' }}>
                  {scorecard.metrics.dmlRisk}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">Bulk Safety</p>
                <p className={`text-lg font-semibold px-3 py-1 rounded-full inline-block ${getRiskColor(scorecard.metrics.bulkSafety)}`} style={{ marginTop: '1.125rem' }}>
                  {scorecard.metrics.bulkSafety}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-sm text-gray-500">CPU Risk</p>
                <p className={`text-lg font-semibold px-3 py-1 rounded-full inline-block ${getRiskColor(scorecard.metrics.cpuRisk)}`} style={{ marginTop: '1.125rem' }}>
                  {scorecard.metrics.cpuRisk}
                </p>
              </div>
            </div>

            {/* Summary */}
            <div className="mt-4 p-4 bg-white rounded-lg">
              <p className="text-gray-700">{scorecard.summary}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('grading')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'grading'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Grading Details
              </button>
              <button
                onClick={() => setActiveTab('issues')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'issues'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Recommended Fixes ({scorecard.issues.length})
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="px-6 py-4">
            {activeTab === 'issues' && (
              <div className="space-y-4">
                {scorecard.issues.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No issues found! This flow follows best practices.</p>
                ) : (
                  scorecard.issues.map((issue, index) => {
                    // Create a mapping from recommendation titles to their likely detector names
                    // Based on the pattern in scorecard-analyzer.ts recommendationMap
                    const recommendationToDetector: Record<string, string> = {
                      'Remove DML from Loops': 'DML Operations in Loops',
                      'Remove Queries from Loops': 'Queries in Loops',
                      'Add Error Handling': 'Missing Error Handling',
                      'Add Flow Description': 'Missing Description',
                      'Reduce Query Count': 'Excessive Queries',
                      'Reduce Flow Complexity': 'High Complexity',
                      'Improve Element Naming': 'Poor Naming Convention'
                    };

                    // Find matching recommendation by detector name
                    const matchingRecommendation = scorecard.recommendations.find(rec => {
                      // Direct match: recommendation was created from this detector
                      return recommendationToDetector[rec.title] === issue.detectorName;
                    });

                    return (
                    <div key={index} className={`border rounded-lg p-4 ${getSeverityColor(issue.severity)}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start gap-2 flex-wrap">
                            <span className={`text-xs font-medium px-2 py-1 rounded ${getSeverityColor(issue.severity)} flex-shrink-0`}>
                              {issue.severity}
                            </span>
                            <span className="text-sm font-bold text-gray-800 flex-shrink-0">{issue.type}</span>
                            <span className="text-sm text-gray-700">{issue.description}</span>
                          </div>
                        </div>
                        {issue.impact && (
                          <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded flex-shrink-0">
                            Impact: {issue.impact}
                          </span>
                        )}
                      </div>

                      {/* Show matching recommendations */}
                      {matchingRecommendation && (
                        <div className="mt-3 ml-2">
                          <p className="text-sm font-medium text-gray-900 mb-1">Recommendations:</p>
                          {matchingRecommendation.specificSteps && matchingRecommendation.specificSteps.length > 0 && (
                            <ul className="space-y-1">
                              {matchingRecommendation.specificSteps.map((step, stepIndex) => (
                                <li key={stepIndex} className="text-sm text-gray-600 ml-4">
                                  • {step.element && <span className="font-medium">{step.element}:</span>}{' '}
                                  {step.helpUrl ? (
                                    <Link
                                      to={step.helpUrl}
                                      className="text-blue-600 hover:text-blue-800 hover:underline"
                                    >
                                      {step.action}
                                    </Link>
                                  ) : (
                                    step.action
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      {/* Show detailed help for hardcoded IDs */}
                      {issue.detectorName === 'Hardcoded Salesforce IDs' && issue.metadata && (
                        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <h5 className="text-sm font-semibold text-yellow-800 mb-2">
                            🚨 Deployment Risk: Fix Required
                          </h5>
                          <p className="text-sm text-yellow-700 mb-3">
                            This flow will break when deployed to other environments.
                          </p>
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-yellow-800">Best Practices:</p>
                            <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700">
                              {issue.metadata.firstType === 'User' && (
                                <li>
                                  For user assignments → <Link to="/help/best-practices/user-assignment" className="text-blue-600 hover:text-blue-800 underline">Use Queues Instead</Link>
                                </li>
                              )}
                              {issue.metadata.firstType === 'Record Type' && (
                                <li>
                                  For record types → <Link to="/help/best-practices/record-type-assignment" className="text-blue-600 hover:text-blue-800 underline">Query by DeveloperName</Link>
                                </li>
                              )}
                              {(issue.metadata.firstType === 'Queue/Group' || issue.metadata.firstType === 'Unknown') && (
                                <li>
                                  For queues/groups → <Link to="/help/best-practices/user-assignment" className="text-blue-600 hover:text-blue-800 underline">Query by DeveloperName</Link>
                                </li>
                              )}
                              <li>
                                For any IDs → <Link to="/help/best-practices/custom-metadata" className="text-blue-600 hover:text-blue-800 underline">Use Custom Metadata</Link>
                              </li>
                            </ul>
                          </div>
                          <div className="mt-3 pt-3 border-t border-yellow-200">
                            <Link to="/help/guides/hardcoded-ids" className="text-sm font-medium text-blue-600 hover:text-blue-800 inline-flex items-center">
                              📖 Complete Guide to Avoiding Hardcoded IDs
                              <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>
                          </div>
                        </div>
                      )}

                      {/* Show detailed help for overly broad entry criteria */}
                      {issue.detectorName === 'Overly Broad Entry Criteria' && issue.metadata && (
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <h5 className="text-sm font-semibold text-blue-800 mb-2">
                            🎯 Optimize Flow Entry Criteria
                          </h5>
                          <p className="text-sm text-blue-700 mb-3">
                            This flow triggers on every record update but only acts when specific fields change. This wastes resources.
                          </p>
                          {issue.metadata.detailsExpanded && (
                            <p className="text-sm text-blue-600 mb-3">
                              {issue.metadata.detailsExpanded}
                            </p>
                          )}
                          {issue.metadata.fields && issue.metadata.fields.length > 0 && (
                            <div className="mb-3 p-2 bg-white rounded border border-blue-200">
                              <p className="text-xs font-mono text-blue-800">
                                Suggested Entry Criteria:
                              </p>
                              <p className="text-xs font-mono text-blue-700 mt-1">
                                ISCHANGED({issue.metadata.fields[0]})
                                {issue.metadata.fields.length > 1 && ` OR ISCHANGED(${issue.metadata.fields[1]})`}
                              </p>
                            </div>
                          )}
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <Link to="/help/best-practices/entry-criteria" className="text-sm font-medium text-blue-600 hover:text-blue-800 inline-flex items-center">
                              📖 Entry Criteria Best Practices
                              <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })
                )}
              </div>
            )}

            {activeTab === 'grading' && scorecard.gradingDetails && (
              <div className="space-y-6">
                {/* Score Calculation Section */}
                <div>
                  <div className="text-lg text-gray-900 mb-4">
                    Starting Score: <span className="font-mono font-bold text-gray-900">100</span>
                  </div>
                  
                  {/* Deduction Items */}
                  {scorecard.gradingDetails.deductions.length === 0 ? (
                    <p className="text-gray-500 mb-4">No deductions - perfect score!</p>
                  ) : (
                    <>
                      {scorecard.gradingDetails.deductions.map((deduction, index) => (
                        <div key={index} className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-bold text-gray-900">{deduction.reason}</span>
                            {deduction.details && (
                              <span className="text-gray-500">({deduction.details})</span>
                            )}
                            <span className={`text-xs px-1.5 py-0.5 rounded lowercase ${getBadgeClasses(deduction.points)}`}>
                              {getImpactBadge(deduction.points)}
                            </span>
                          </div>
                          <span className={`font-mono ${deduction.points < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            {deduction.points}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                  
                  {/* Separator Line */}
                  <div className="border-t border-gray-300 my-3" />
                  
                  {/* Total */}
                  <div className="flex justify-between font-semibold mb-4">
                    <span className="text-gray-900">Total Deductions:</span>
                    <span className="font-mono text-red-600">
                      {scorecard.gradingDetails.deductions.reduce((sum, d) => sum + d.points, 0)}
                    </span>
                  </div>
                  
                  {/* Final Score and Grade */}
                  <div className="flex justify-between items-center text-lg">
                    <span className="text-gray-900">
                      Final Score: <span className={`font-mono font-bold ${getGradeColor(scorecard.gradingDetails.grade).split(' ')[0]}`}>{scorecard.gradingDetails.finalScore}</span>
                    </span>
                    <span className="text-gray-900">
                      Grade: <span className={`font-bold ${getGradeColor(scorecard.gradingDetails.grade).split(' ')[0]}`}>{scorecard.gradingDetails.grade === 'F' ? 'I' : scorecard.gradingDetails.grade}</span>
                    </span>
                  </div>
                </div>
                
                {/* Grade Scale */}
                <div>
                  <h4 className="font-semibold mb-3">Grading Scale</h4>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { grade: 'A', range: '90-100' },
                      { grade: 'B', range: '80-89' },
                      { grade: 'C', range: '70-79' },
                      { grade: 'D', range: '60-69' },
                      { grade: 'Incomplete', range: 'Below 60', value: 'I' }
                    ].map(({ grade, range, value }) => {
                      const gradeValue = value || grade;
                      const isCurrentGrade = gradeValue === scorecard.gradingDetails.grade;
                      return (
                        <div
                          key={grade}
                          className={`text-center p-3 rounded-lg text-white ${
                            isCurrentGrade
                              ? 'ring-2 ring-offset-2 ring-gray-400' 
                              : ''
                          } ${getGradeBackgroundColor(gradeValue)}`}
                        >
                          <div className="font-bold text-lg">{grade}</div>
                          <div className="text-xs opacity-90">{range}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 bg-gray-50 text-right text-xs text-gray-500">
            Analyzed at {new Date(scorecard.analyzedAt).toLocaleString()}
          </div>
        </>
      )}
    </div>
  );
}